import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { IepFormContext } from '../../../../utils/iepFormProvider'
import { useQuery } from '@apollo/client'
import { QUERY_STUDENT_COMMON_SIS } from '../../../../operations/queries/forms/SIS'
import { QUERY_FORMTAMES2_VERSION } from '../../../../operations/queries/forms/tames2'
import dayjs from 'dayjs'
import FormErrorProvider, { FormErrorContext } from '../errorProvider'
import FormContainer from '../../../FormContainer'
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Input,
  Form,
  Radio,
  Typography
} from 'antd'
import FormCollaboration from '../../../FormCollaboration'
import Title from 'antd/lib/typography/Title'
import StudentFullNameSIS from '../sis/StudentFullNameSIS'
import StudentDobSIS from '../sis/StudentDobSIS'
import StudentFormActions from '../StudentFormActions'
import { PRINT_FORMTAMES2 } from '../../../../operations/mutations/forms/tames2'
import { PlusOutlined } from '@ant-design/icons'
import Editor from '../../../elements/common/Editor'
import FormItem from 'antd/lib/form/FormItem'

const { Paragraph, Text } = Typography

const FormRecord = ({
  data,
  studentFormId,
  studentId,
  formId,
  schoolId,
  studentMeetingId,
  sis,
  versionId
}) => {
  const [form] = Form.useForm()

  const { itemErrors, formError } = useContext(FormErrorContext)

  const getFormFieldValues = () => {
    const formFieldValues = form.getFieldsValue()
    const domains = formFieldValues.domains.map(
      ({ __typename, ...props }) => props
    )

    return {
      ...formFieldValues,
      studentFormId: studentFormId,
      formId: formId,
      studentId: studentId,
      schoolId: schoolId,
      studentMeetingId: studentMeetingId,
      domains: domains
    }
  }

  const formatFormFieldUpdate = (fieldName, fieldValue) => {
    const update = Object.assign({})
    switch (fieldName) {
      case 'formDate':
      case 'parentConsentDate':
        update[fieldName] = fieldValue ? dayjs(fieldValue) : ''
        break
      case 'doNotIncludeReport':
        update[fieldName] = fieldValue === 'null' ? null : fieldValue === 'true'
        break
      case 'domains':
        const domains = JSON.parse(fieldValue)
        domains.forEach(
          domain =>
            (domain.domainDateCompleted = dayjs(domain.domainDateCompleted))
        )
        update[fieldName] = domains
        break
      default:
        update[fieldName] = fieldValue
    }
    return update
  }

  const handleFieldCollaborationUpdate = (fieldName, fieldValue) => {
    const update = formatFormFieldUpdate(fieldName, fieldValue)
    form.setFieldsValue(update)
  }

  return (
    <>
      <FormCollaboration
        form={form}
        itemErrors={itemErrors}
        studentFormId={studentFormId}
        handleUpdate={handleFieldCollaborationUpdate}
      >
        {({
          handleMouseEvents,
          handleFocusEvents,
          handleEditorEvents,
          formDisabledState,
          canSave,
          handleActionEvents,
          validationProps
        }) => (
          <Form
            form={form}
            id='studentFormTames2'
            name='studentFormTames2'
            initialValues={data}
            preserve
            layout='vertical'
            className='page-padding-leftRight page-padding-topBottom form-labels-bold'
          >
            <Title level={4} className='center'>
              EVALUATION COMPONENTS
            </Title>
            <Form.Item
              label='Form Created Date'
              name='formDate'
              {...validationProps('formDate')}
            >
              <DatePicker
                {...handleFocusEvents}
                disabled={formDisabledState.formDate}
                format={[
                  'MM-DD-YYYY',
                  'MM/DD/YYYY',
                  'MM.DD.YY',
                  'M-D-YYYY',
                  'M/D/YYYY',
                  'M.D.YYYY',
                  'MM.DD.YYYY',
                  'MM-DD-YY',
                  'MM/DD/YY',
                  'M-D-YY',
                  'M/D/YY',
                  'M.D.YY',
                  'MMDDYYYY',
                  'MMDDYY'
                ]}
              />
            </Form.Item>

            <Card
              title='Student SIS Data'
              style={{
                marginBottom: 30
              }}
            >
              <Alert
                message='SIS fields are locked, please contact administrator in case of error.'
                type='warning'
                style={{ marginBottom: 20 }}
              />
              <StudentFullNameSIS
                form={form}
                sis={sis}
                id={studentId}
                initialValue={data.studentFullName}
                error={itemErrors.studentFullName}
              />
              <StudentDobSIS
                form={form}
                sis={sis}
                id={studentId}
                initialValue={data.studentDob}
                error={itemErrors.studentDob}
              />
            </Card>

            <Card
              title='Details'
              style={{
                marginBottom: 30
              }}
            >
              <FormItem
                label='Parent Consent Granted?'
                name='parentConsentGranted'
                {...validationProps('parentConsentGranted')}
              >
                <Radio.Group
                  defaultValue
                  defaultChecked='n/a'
                  buttonStyle='solid'
                  {...handleMouseEvents}
                  disabled={formDisabledState.parentConsentGranted}
                  name='parentConsentGranted'
                >
                  <Radio.Button value='yes'>Yes</Radio.Button>
                  <Radio.Button value='no'>No</Radio.Button>
                  <Radio.Button value='n/a'>Decision Delayed</Radio.Button>
                </Radio.Group>
              </FormItem>

              <Form.Item
                label='Date of Parent Consent'
                name='parentConsentDate'
                {...validationProps('parentConsentDate')}
              >
                <DatePicker
                  {...handleFocusEvents}
                  disabled={formDisabledState.parentConsentDate}
                  format={[
                    'MM-DD-YYYY',
                    'MM/DD/YYYY',
                    'MM.DD.YY',
                    'M-D-YYYY',
                    'M/D/YYYY',
                    'M.D.YYYY',
                    'MM.DD.YYYY',
                    'MM-DD-YY',
                    'MM/DD/YY',
                    'M-D-YY',
                    'M/D/YY',
                    'M.D.YY',
                    'MMDDYYYY',
                    'MMDDYY'
                  ]}
                />
              </Form.Item>

              <Form.Item
                label='Do not include report for evaluation components'
                name='doNotIncludeReport'
                {...validationProps('doNotIncludeReport')}
              >
                <Radio.Group
                  {...handleMouseEvents}
                  buttonStyle='solid'
                  disabled={formDisabledState.doNotIncludeReport}
                  name='doNotIncludeReport'
                >
                  <Radio value>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>
            </Card>

            <Card
              title='Domain(s) Information'
              style={{
                marginBottom: '30px'
              }}
            >
              <Input.Group
                onFocus={() => {
                  handleEditorEvents.onFocus('domains')
                }}
                onBlur={() => {
                  handleEditorEvents.onBlur(
                    'domains',
                    JSON.stringify(form.getFieldValue('domains'))
                  )
                }}
              >
                <Form.List name='domains' {...validationProps('domains')}>
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field, index) => (
                        <div key={field.key}>
                          <Card
                            style={{
                              marginBottom: '30px',
                              borderStyle: 'dashed'
                            }}
                          >
                            <Form.Item
                              {...field}
                              label='Domain'
                              name={[field.name, 'domain']}
                              key={[field.fieldKey, 'domain']}
                            >
                              <Input disabled={formDisabledState.domains} />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              label='Date Completed'
                              name={[field.name, 'domainDateCompleted']}
                              key={[field.fieldKey, 'domainDateCompleted']}
                            >
                              <DatePicker
                                {...handleFocusEvents}
                                disabled={formDisabledState.domains}
                                format={[
                                  'MM-DD-YYYY',
                                  'MM/DD/YYYY',
                                  'MM.DD.YY',
                                  'M-D-YYYY',
                                  'M/D/YYYY',
                                  'M.D.YYYY',
                                  'MM.DD.YYYY',
                                  'MM-DD-YY',
                                  'MM/DD/YY',
                                  'M-D-YY',
                                  'M/D/YY',
                                  'M.D.YY',
                                  'MMDDYYYY',
                                  'MMDDYY'
                                ]}
                              />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              label='Evaluation Summary'
                              name={[field.name, 'domainEvaluationSummary']}
                              key={[field.fieldKey, 'domainEvaluationSummary']}
                            >
                              <Editor
                                onFocus={() => { }}
                                onBlur={() => { }}
                                disabled={formDisabledState.domains}
                              />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              label='Evaluation Report'
                              name={[field.name, 'domainEvaluationReport']}
                              key={[field.fieldKey, 'domainEvaluationReport']}
                            >
                              <Editor
                                onFocus={() => { }}
                                onBlur={() => { }}
                                disabled={formDisabledState.domains}
                              />
                            </Form.Item>
                            <Button
                              danger
                              disabled={formDisabledState.domains}
                              onClick={() => {
                                remove(field.name)
                                handleEditorEvents.onBlur(
                                  'domains',
                                  JSON.stringify(form.getFieldValue('domains'))
                                )
                              }}
                            >
                              Remove Domain
                            </Button>
                          </Card>
                        </div>
                      ))}

                      <Form.Item>
                        {
                          // Default value below prevents Quill from breaking the render
                        }
                        <Button
                          disabled={formDisabledState.domains}
                          type='dashed'
                          onClick={() =>
                            add({
                              domain: '',
                              domainDateCompleted: dayjs(Date.now()),
                              domainEvaluationSummary: '',
                              domainEvaluationReport: ''
                            })
                          }
                          block
                          icon={<PlusOutlined />}
                        >
                          Add Domain
                        </Button>
                      </Form.Item>
                      <Text type='warning'>
                        {validationProps('domains').help}
                      </Text>
                    </>
                  )}
                </Form.List>
              </Input.Group>
            </Card>

            {formError && <Paragraph type='danger'>{formError}</Paragraph>}
            <StudentFormActions
              query={PRINT_FORMTAMES2}
              extractDataProp='printStudentFormTames2'
              canSave={canSave}
              handleActionEvents={handleActionEvents}

              getFormFieldValues={getFormFieldValues}
            />
          </Form>
        )}
      </FormCollaboration>
    </>
  )
}

FormRecord.propTypes = {
  data: PropTypes.object.isRequired,
  studentFormId: PropTypes.object.isRequired,
  studentId: PropTypes.string.isRequired,
  formId: PropTypes.string.isRequired,
  schoolId: PropTypes.string.isRequired,
  sis: PropTypes.object.isRequired,
  versionId: PropTypes.string.isRequired,
  studentMeetingId: PropTypes.string.isRequired
}

const StudentFormTames2 = () => {
  // get current form information
  const { currentStudentInfo, currentEditableForm, studentMeetingId } =
    useContext(IepFormContext)
  const { studentId, schoolId } = currentStudentInfo
  const { studentFormId, formId, versionId } = currentEditableForm

  // Load SIS Data / Form Version Data
  const { loading: studentLoading, data: studentData } = useQuery(
    QUERY_STUDENT_COMMON_SIS,
    { variables: { id: studentId } }
  )
  const { loading: formLoading, data: formData } = useQuery(
    QUERY_FORMTAMES2_VERSION,
    {
      variables: {
        studentFormId: studentFormId,
        studentId: studentId,
        schoolId: schoolId,
        formId: formId,
        studentMeetingId: studentMeetingId
      }
    }
  )

  const createInitialFormData = (studentInfo, versionInfo) => {
    let initialValues = {}

    if (!studentInfo || !versionInfo) return initialValues

    const { studentFormTames2: form } = versionInfo
    const { student } = studentInfo
    const { dateOfBirth } = student
    const { formDate, studentFullName, studentDob, parentConsentDate } = form
    const domains = form.domains.map(domain => {
      const { domainDateCompleted } = domain
      return {
        ...domain,
        domainDateCompleted: domainDateCompleted
          ? dayjs(domainDateCompleted)
          : dayjs(Date.now())
      }
    })
    initialValues = {
      ...form,
      formDate: formDate ? dayjs(formDate) : dayjs(Date.now()),
      studentFullName: studentFullName || student.fullName || '',
      studentDob: studentDob
        ? dayjs(studentDob).format('YYYY-MM-DD')
        : dateOfBirth
          ? dayjs(dateOfBirth).format('YYYY-MM-DD')
          : dayjs(Date.now()).format('YYYY-MM-DD'),
      parentConsentDate: parentConsentDate
        ? dayjs(parentConsentDate)
        : dayjs(Date.now()),
      domains: domains
    }

    return initialValues
  }
  return (
    <FormErrorProvider>
      <FormContainer
        loading={studentLoading || formLoading}
        form={
          <FormRecord
            data={createInitialFormData(studentData, formData)}
            studentFormId={studentFormId}
            studentId={studentId}
            formId={formId}
            schoolId={schoolId}
            studentMeetingId={studentMeetingId}
            sis={studentData}
            versionId={studentFormId}
          />
        }
      />
    </FormErrorProvider>
  )
}
export default StudentFormTames2
